<template>
  <div class="catalog-filters">
    <BaseLocationsAutocomplete
      v-model="station"
      :label="$t('pick_up_location')"
      :placeholder="$t('find_station')"
      class="col-span-full"
      :disabled="loading"
    />
    <BaseMonthsPicker
      v-model="months"
      :label="$t('duration')"
      :disabled="loading"
      autofill
      @loaded="onMonthsLoaded"
    />
  </div>
</template>

<script setup lang="ts">
import { watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import BaseLocationsAutocomplete from '~/components/forms/BaseLocationsAutocomplete.vue';
import BaseMonthsPicker from '~/components/forms/BaseMonthsPicker.vue';
import { useGTM } from '~/composables';
import { useRoute, useRouter, onBeforeRouteLeave } from '#imports';
import { useSubscriptionCatalogStore } from '~/stores/subscriptionCatalogStore';
import { useCartStore } from '~/stores/cartStore';
import type { Station } from '~/types';
import { sanitizeQueryParams, serializeQueryParams } from '~/lib/utils';
import { TRACKING_EVENTS } from '~/lib/constants';

interface Props {
  loading?: boolean
}

export type RouteParams = {
  station_id?: string,
  months?: string,
};

export interface ExposedProps {
  init: (p: RouteParams) => Promise<void>
}

defineProps<Props>();
// eslint-disable-next-line func-call-spacing
const emits = defineEmits<{
  (e: 'fetch', value: string): void
}>();

defineExpose<ExposedProps>({
  init,
});

const { triggerEvent } = useGTM();
const STATION_IDENTIFIER = 'id';
const QUERY_PARAMS_WHITE_LIST = ['station_id', 'months'];
const router = useRouter();
const route = useRoute();

const { selectedBooking } = storeToRefs(useCartStore());
const { months, station } = storeToRefs(useSubscriptionCatalogStore());

const cancelWatch = watch(() => route.query, async (updatedQueryParams) => {
  await init(updatedQueryParams);
}, { immediate: true });

async function init (qParams: RouteParams) {
  const sanitizedPayload = sanitizeQueryParams(qParams, QUERY_PARAMS_WHITE_LIST);
  emits('fetch', serializeQueryParams(sanitizedPayload));

  if (sanitizedPayload.months) {
    months.value = sanitizedPayload.months;
  }

  if (sanitizedPayload.station_id) {
    station.value = {
      ...(station?.value),
      [STATION_IDENTIFIER]: sanitizedPayload?.station_id || '',
    };
  }

  await nextTick();
  startWatchingSearchParams();
}

function onMonthsLoaded () {
  watch(months, (newMonths) => {
    setQueryParams('months', newMonths as string);
    selectedBooking.value.monthsLong = String(newMonths);
    triggerEvent(`${TRACKING_EVENTS.CLICK_VEHICLE_LIST}change_month`, { months: newMonths });
  });
}

async function startWatchingSearchParams () {
  watch(station, (newStation, oldStation) => {
    selectedBooking.value.station = newStation;
    const stationId = newStation?.id;
    setQueryParams('station_id', stationId);
    if (stationId) {
      return triggerEvent(`${TRACKING_EVENTS.CLICK_VEHICLE_LIST}_change_station`, {
        station: String(stationId),
      });
    }

    if (String(oldStation?.id) === '0') return;

    triggerEvent(`${TRACKING_EVENTS.CLICK_VEHICLE_LIST}_remove_station`, {
      station: String(oldStation?.id),
    });
  });
}

async function setQueryParams (key: string, value: string | number | Record<string, any> | null) {
  const result = structuredClone(route.query);
  if (key === 'station') {
    const station = value as Station;
    const stationId = station?.id;
    result.station_id = stationId as string;
    delete result.station;
  }
  const sanitizedQuery = sanitizeQueryParams({ ...result, [key]: value }, QUERY_PARAMS_WHITE_LIST);

  await router.push({
    query: sanitizedQuery as Record<string, any>,
  });
}

onBeforeRouteLeave(() => {
  cancelWatch && cancelWatch();
});
</script>

<style scoped>
.catalog-filters {
  @apply flex flex-col gap-4;
}
</style>
